import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success('Message envoyé avec succès !');
    } catch (error) {
      toast.error('Erreur lors de l\'envoi du message');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#09090B] py-12">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-zinc-900/50 border-zinc-800 text-center">
            <CardContent className="py-12">
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="text-green-500" size={40} />
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">Message Envoyé !</h2>
              <p className="text-zinc-400 mb-8">
                Merci de nous avoir contactés. Nous vous répondrons dans les plus brefs délais.
              </p>
              <Button 
                onClick={() => { setSubmitted(false); setFormData({ name: '', email: '', phone: '', subject: '', message: '' }); }} 
                className="bg-red-600 hover:bg-red-700"
              >
                Envoyer un autre message
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B]">
      {/* Hero */}
      <section className="bg-gradient-to-b from-zinc-900 to-[#09090B] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold text-white mb-4">
            CONTACTEZ-<span className="text-red-500">NOUS</span>
          </h1>
          <p className="text-xl text-zinc-400">Une question ? N'hésitez pas à nous contacter</p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Contact Info */}
            <div className="lg:col-span-1 space-y-6">
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-red-600/20 rounded-full flex items-center justify-center">
                      <Phone className="text-red-500" size={24} />
                    </div>
                    <div>
                      <p className="text-zinc-500 text-sm">Téléphone</p>
                      <a href="tel:0805032032" className="text-white text-lg font-bold hover:text-red-500">
                        0805 032 032
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-red-600/20 rounded-full flex items-center justify-center">
                      <Mail className="text-red-500" size={24} />
                    </div>
                    <div>
                      <p className="text-zinc-500 text-sm">Email</p>
                      <a href="mailto:info@lehubatelier.fr" className="text-white text-lg font-bold hover:text-red-500">
                        info@lehubatelier.fr
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-red-600/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin className="text-red-500" size={24} />
                    </div>
                    <div>
                      <p className="text-zinc-500 text-sm">Adresse</p>
                      <p className="text-white text-lg font-bold">
                        142 boulevard Murat<br />75016 Paris
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-red-600/20 rounded-full flex items-center justify-center">
                      <Clock className="text-red-500" size={24} />
                    </div>
                    <div>
                      <p className="text-zinc-500 text-sm">Horaires</p>
                      <p className="text-white font-bold">Lundi - Samedi</p>
                      <p className="text-zinc-400">8h00 - 19h30</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="p-8">
                  <h2 className="text-2xl font-bold text-white mb-6">Envoyez-nous un message</h2>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-zinc-300">Nom complet *</Label>
                        <Input
                          required
                          placeholder="Votre nom"
                          value={formData.name}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                          className="bg-zinc-950 border-zinc-800 text-white mt-2"
                          data-testid="contact-name"
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-300">Téléphone *</Label>
                        <Input
                          required
                          type="tel"
                          placeholder="06 12 34 56 78"
                          value={formData.phone}
                          onChange={(e) => setFormData({...formData, phone: e.target.value})}
                          className="bg-zinc-950 border-zinc-800 text-white mt-2"
                          data-testid="contact-phone"
                        />
                      </div>
                    </div>

                    <div>
                      <Label className="text-zinc-300">Email *</Label>
                      <Input
                        required
                        type="email"
                        placeholder="votre@email.com"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="bg-zinc-950 border-zinc-800 text-white mt-2"
                        data-testid="contact-email"
                      />
                    </div>

                    <div>
                      <Label className="text-zinc-300">Sujet *</Label>
                      <Input
                        required
                        placeholder="Objet de votre message"
                        value={formData.subject}
                        onChange={(e) => setFormData({...formData, subject: e.target.value})}
                        className="bg-zinc-950 border-zinc-800 text-white mt-2"
                        data-testid="contact-subject"
                      />
                    </div>

                    <div>
                      <Label className="text-zinc-300">Message *</Label>
                      <Textarea
                        required
                        placeholder="Votre message..."
                        value={formData.message}
                        onChange={(e) => setFormData({...formData, message: e.target.value})}
                        className="bg-zinc-950 border-zinc-800 text-white mt-2 min-h-[150px]"
                        data-testid="contact-message"
                      />
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full bg-red-600 hover:bg-red-700 h-12"
                      disabled={loading}
                      data-testid="contact-submit"
                    >
                      {loading ? 'Envoi en cours...' : (
                        <>
                          <Send className="mr-2" size={18} />
                          Envoyer le message
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Map */}
          <div className="mt-12">
            <Card className="bg-zinc-900/50 border-zinc-800 overflow-hidden">
              <iframe
                title="Le Hub Atelier Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2625.9876!2d2.2580!3d48.8420!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDjCsDUwJzMxLjIiTiAywrAxNScyOC44IkU!5e0!3m2!1sfr!2sfr!4v1234567890"
                width="100%"
                height="400"
                style={{ border: 0, filter: 'grayscale(100%) invert(92%) contrast(83%)' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}
